import { GameStateHook } from '../utils/useGameState';
import { ACHIEVEMENTS } from '../game/achievements';

interface Props {
  onBack: () => void;
  gameState: GameStateHook;
}

export function Profile({ onBack, gameState }: Props) {
  const user = gameState.user;

  return (
    <div style={styles.container}>
      <div style={styles.topBar}>
        <button style={styles.backBtn} onClick={onBack}>Back</button>
        <h2 style={styles.title}>Profile</h2>
        <div style={{ width: 50 }} />
      </div>

      <div style={styles.content}>
        <div style={styles.avatar}>
          <span style={styles.avatarEmoji}>@</span>
        </div>
        <h3 style={styles.name}>{user?.displayName || user?.username || 'Player'}</h3>

        <div style={styles.statsGrid}>
          <div style={styles.statCard}>
            <span style={styles.statValue}>{gameState.levelsCompleted}</span>
            <span style={styles.statLabel}>Levels Done</span>
          </div>
          <div style={styles.statCard}>
            <span style={styles.statValue}>{gameState.totalStars}</span>
            <span style={styles.statLabel}>Total Stars</span>
          </div>
          <div style={gameState.streak > 0 ? styles.streakCard : styles.statCard}>
            <span style={gameState.streak > 0 ? styles.streakValue : styles.statValue}>
              {gameState.streak}
            </span>
            <span style={gameState.streak > 0 ? styles.streakLabel : styles.statLabel}>Day Streak</span>
          </div>
        </div>

        <div style={styles.section}>
          <h4 style={styles.sectionTitle}>Worlds Unlocked</h4>
          <div style={styles.purchaseList}>
            <div style={styles.purchaseItem}>
              <span>Warehouse Basics</span>
              <span style={styles.purchaseStatusActive}>Unlocked</span>
            </div>
            <div style={styles.purchaseItem}>
              <span>Shipping Yard</span>
              <span style={gameState.isWorldUnlocked(2) ? styles.purchaseStatusActive : styles.purchaseStatus}>
                {gameState.isWorldUnlocked(2) ? 'Unlocked' : 'Locked'}
              </span>
            </div>
            <div style={styles.purchaseItem}>
              <span>Factory Floor</span>
              <span style={gameState.isWorldUnlocked(3) ? styles.purchaseStatusActive : styles.purchaseStatus}>
                {gameState.isWorldUnlocked(3) ? 'Unlocked' : 'Locked'}
              </span>
            </div>
          </div>
        </div>

        <div style={styles.section}>
          <h4 style={styles.sectionTitle}>Achievements ({gameState.achievements.size} / {ACHIEVEMENTS.length})</h4>
          <div style={styles.achievementGrid}>
            {ACHIEVEMENTS.map(a => {
              const unlocked = gameState.achievements.has(a.id);
              return (
                <div key={a.id} style={unlocked ? styles.achievementUnlocked : styles.achievementLocked}>
                  <span style={unlocked ? styles.achievementName : styles.achievementNameLocked}>{a.name}</span>
                  <span style={unlocked ? styles.achievementDesc : styles.achievementDescLocked}>{a.description}</span>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}

const styles: Record<string, any> = {
  container: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    minHeight: 0,
    overflow: 'hidden',
  },
  topBar: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '12px 16px',
  },
  backBtn: {
    background: 'none',
    border: 'none',
    color: '#4ecdc4',
    fontSize: '16px',
    cursor: 'pointer',
    fontFamily: 'inherit',
    padding: '8px',
  },
  title: {
    margin: 0,
    fontSize: '18px',
    fontWeight: '600',
    color: '#eee',
  },
  content: {
    flex: 1,
    padding: '16px',
    overflowY: 'auto',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '20px',
  },
  avatar: {
    width: '80px',
    height: '80px',
    borderRadius: '50%',
    background: 'rgba(78, 205, 196, 0.2)',
    border: '3px solid #4ecdc4',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  avatarEmoji: {
    fontSize: '32px',
    color: '#4ecdc4',
  },
  name: {
    margin: 0,
    fontSize: '20px',
    fontWeight: '700',
    color: '#eee',
  },
  statsGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr 1fr',
    gap: '12px',
    width: '100%',
    maxWidth: '320px',
  },
  statCard: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '4px',
    padding: '16px',
    background: 'rgba(255,255,255,0.05)',
    borderRadius: '12px',
  },
  statValue: {
    fontSize: '24px',
    fontWeight: '700',
    color: '#fff',
  },
  statLabel: {
    fontSize: '11px',
    color: '#888',
    textTransform: 'uppercase',
    letterSpacing: '1px',
  },
  streakCard: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '4px',
    padding: '16px',
    background: 'rgba(255, 160, 50, 0.12)',
    borderRadius: '12px',
    border: '1px solid rgba(255, 160, 50, 0.25)',
  },
  streakValue: {
    fontSize: '24px',
    fontWeight: '700',
    color: '#ffa032',
  },
  streakLabel: {
    fontSize: '11px',
    color: '#ffa032',
    textTransform: 'uppercase',
    letterSpacing: '1px',
  },
  section: {
    width: '100%',
    maxWidth: '320px',
    marginTop: '8px',
  },
  sectionTitle: {
    fontSize: '14px',
    fontWeight: '600',
    color: '#eee',
    margin: '0 0 12px',
  },
  purchaseList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
  purchaseItem: {
    display: 'flex',
    justifyContent: 'space-between',
    padding: '12px',
    background: 'rgba(255,255,255,0.04)',
    borderRadius: '10px',
    fontSize: '14px',
    color: '#eee',
  },
  purchaseStatus: {
    color: '#888',
    fontSize: '13px',
  },
  purchaseStatusActive: {
    color: '#4ecdc4',
    fontSize: '13px',
  },
  achievementGrid: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
  achievementUnlocked: {
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
    padding: '12px',
    background: 'rgba(78, 205, 196, 0.08)',
    borderRadius: '10px',
    border: '1px solid rgba(78, 205, 196, 0.3)',
  },
  achievementLocked: {
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
    padding: '12px',
    background: 'rgba(255,255,255,0.03)',
    borderRadius: '10px',
    opacity: 0.5,
  },
  achievementName: {
    fontSize: '14px',
    fontWeight: '600',
    color: '#4ecdc4',
  },
  achievementNameLocked: {
    fontSize: '14px',
    fontWeight: '600',
    color: '#888',
  },
  achievementDesc: {
    fontSize: '12px',
    color: '#aaa',
  },
  achievementDescLocked: {
    fontSize: '12px',
    color: '#666',
  },
};
